import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SwoodleLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import "./login.scss";
import TextInput from "../SignUp/TextInput";
import { NavLink, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { loginSchema } from "../../../utils/validationSchemas";
import { ReactComponent as CheckedBox } from "../../../Assets/Icons/checkedBox.svg";
import { ReactComponent as UncheckedBox } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import { login } from "../../../services/authService";
import {
  setLoginData,
  setUserData,
  clearAuth,
} from "../../../redux/slices/authSlice";
import { getUserSubscription } from "../../../services/subscriptionService";
import Loader from "../Loader";
import { getOnboardingStatus } from "../../../services/fundraiserService";
import { getFcmToken } from "../../../utils/connectStore";
import axios from "../../../utils/axios";
import PrimaryButton from "../PrimaryButton";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
import SwoodleFooter from "../SwoodleFooter";
import LoginHeader from "../LoginHeader";

const DELETED_MERCHANT_MESSAGE =
  "Your Account got deleted. Please Contact support.";
const DISABLED_MERCHANT_MESSAGE =
  "Your Account is Disabled. Please contact Support for further information.";
const DISABLED_FUNDRAISER_MESSAGE =
  "Your account is disabled. Please contact support.";
const DELETED_FUNDRAISER_MESSAGE =
  "Your account have been deleted. Please Contact Support";
const DECLINED_FUNDRAISER_MESSAGE =
  "Your account has been declined. Please contact support.";
const REJECTED_FUNDRAISER_MESSAGE =
  "Your account has been rejected. Please contact support.";

const responsiveShellStyle = {
  width: "100%",
  maxWidth: "min(1920px, calc(100vw - 2vw))",
  margin: "0 auto",
};

const LoginSection = () => {
  const dispatch = useDispatch();
  const subLoading = useSelector((state) => state.subscriptionReducer.loading);
  const authLoading = useSelector((state) => state.authReducer.loading);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [checked, setChecked] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [footerReady, setFooterReady] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setFooterReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  const onChecked = () => {
    setChecked(!checked);
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const clearFundraiserSessionState = () => {
    sessionStorage.removeItem("showAccountPendingModal");
    sessionStorage.removeItem("fundraiser_stripe_complete");
    sessionStorage.removeItem("fundraiser_block_status");
    sessionStorage.removeItem("fundraiser_verified");
    sessionStorage.removeItem("skip_auto_redirect");
  };

  const normalizeMerchantStatus = (status) =>
    String(status || "").toLowerCase();

  const isBlockedMerchantStatus = (status) => {
    const normalizedStatus = normalizeMerchantStatus(status);
    return ["deleted", "disabled", "declined", "rejected"].includes(
      normalizedStatus,
    );
  };

  const normalizeAuthErrorMessage = (message, merchantProfiles = []) => {
    const normalizedMessage = typeof message === "string" ? message.trim() : "";
    const profiles = Array.isArray(merchantProfiles) ? merchantProfiles : [];
    const hasDeletedMerchant = profiles.some(
      (profile) => normalizeMerchantStatus(profile?.status) === "deleted",
    );
    const hasDisabledMerchant = profiles.some((profile) =>
      ["disabled", "declined", "rejected"].includes(
        normalizeMerchantStatus(profile?.status),
      ),
    );

    if (hasDeletedMerchant) {
      return DELETED_MERCHANT_MESSAGE;
    }

    if (hasDisabledMerchant) {
      return DISABLED_MERCHANT_MESSAGE;
    }

    if (
      normalizedMessage &&
      normalizedMessage.toLowerCase() ===
        "some of your merchant accounts are still in progress or require further action. please contact support."
    ) {
      return DELETED_MERCHANT_MESSAGE;
    }

    if (
      normalizedMessage &&
      normalizedMessage.toLowerCase() ===
        "your account is not active at the moment. please contact admin."
    ) {
      return DISABLED_MERCHANT_MESSAGE;
    }

    return normalizedMessage;
  };

  const getActiveMerchantProfile = (merchantProfiles = []) => {
    const profiles = Array.isArray(merchantProfiles) ? merchantProfiles : [];
    const activeProfile =
      profiles.find((profile) =>
        ["approved", "live"].includes(normalizeMerchantStatus(profile?.status)),
      ) ||
      profiles.find((profile) => !isBlockedMerchantStatus(profile?.status)) ||
      null;

    if (activeProfile) {
      return activeProfile;
    }

    return null;
  };

  const navigation = async (role, user) => {
    // NOTE: Approval status check is done in onSubmit BEFORE calling navigation()
    // If we reach here, the fundraiser is already approved (or not a fundraiser)
    switch (role) {
      case "merchant":
        navigate("/merchant/dashboard");
        break;
      case "fundraiser":
        // Skip Stripe check - already verified in login flow
        // Just navigate directly to dashboard (dashboard will verify on mount)
        navigate("/fundraiser/dashboard");
        break;
      case "admin":
        navigate("/admin/dashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setFieldError }) => {
      clearFundraiserSessionState();
      dispatch(clearAuth());
      setAuthErrorMessage("");
      setIsCheckingStatus(true); // Show loader while checking
      try {
        const credentials = {
          email: values.email,
          password: values.password,
          fcmToken: getFcmToken(),
        };

        const loginWithRedux = async () => {
          // Store the token we already received from the direct API call
          // No need to call the login API again via Redux thunk
          if (promiseResult?.data?.token) {
            dispatch(setUserData(promiseResult.data));

            // ALSO save directly to localStorage as backup
            try {
              const existingState = JSON.parse(
                localStorage.getItem("persist:root") || "{}",
              );
              const authState =
                typeof existingState.authReducer === "string"
                  ? JSON.parse(existingState.authReducer)
                  : existingState.authReducer;

              const updatedAuthState = {
                ...authState,
                tokenData: promiseResult.data,
              };

              localStorage.setItem(
                "persist:root",
                JSON.stringify({
                  ...existingState,
                  authReducer: JSON.stringify(updatedAuthState),
                }),
              );
            } catch (e) {}

            // Small delay to ensure redux-persist saves the token
            await new Promise((resolve) => setTimeout(resolve, 100));

            dispatch(
              setLoginData({
                email: credentials.email,
                password: credentials.password,
                fcmToken: credentials.fcmToken,
              }),
            );
          } else {
            throw new Error("No token received from login");
          }
        };

        // Step 1: Call login API DIRECTLY (without Redux) to get token without setting it in state
        // This prevents CommonRoutes from redirecting before we check status
        let loginResponse;
        try {
          loginResponse = await axios.post("auth/login", {
            email: credentials.email,
            password: credentials.password,
            fcmToken: credentials.fcmToken,
          });
        } catch (loginError) {
          setIsCheckingStatus(false);
          const errorMsg = normalizeAuthErrorMessage(
            loginError?.response?.data?.message ||
              loginError?.message ||
              "Invalid credentials",
          );
          setAuthErrorMessage(errorMsg);
          setFieldError("password", errorMsg);
          formik.setTouched({ password: true }, false);
          return;
        }

        const promiseResult = loginResponse?.data;
        if (!promiseResult || promiseResult?.success !== true) {
          setIsCheckingStatus(false);
          const errorMsg = normalizeAuthErrorMessage(
            promiseResult?.message || "Invalid credentials",
          );
          setAuthErrorMessage(errorMsg);
          setFieldError("password", errorMsg);
          formik.setTouched({ password: true }, false);
          return;
        }

        const userRole = promiseResult?.data?.user?.role;
        const normalizedRole =
          typeof userRole === "string" ? userRole.toLowerCase() : userRole;

        // Check: If user role is "user" or "member", block login
        // This is the correct way to check - after successful login, check the actual role
        if (normalizedRole === "user" || normalizedRole === "member") {
          setIsCheckingStatus(false);
          setFieldError(
            "password",
            "This email is registered as a member account. Please open the mobile app and log in to continue.",
          );
          return;
        }

        // Step 2: For fundraisers ONLY - check approval status first so blocked accounts
        // never enter dashboard/welcome flows or show stale pending state.
        if (normalizedRole === "fundraiser") {
          const tempToken = promiseResult?.data?.token;
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: {
                Authorization: `Bearer ${tempToken}`,
                "api-key": process.env.REACT_APP_API_KEY,
                "Cache-Control": "no-cache",
              },
              params: { _: Date.now() },
            });
            const fundraiserProfile = checkResponse?.data?.data;
            const profileStatusRaw = fundraiserProfile?.status;
            const profileStatus =
              typeof profileStatusRaw === "string"
                ? profileStatusRaw.toLowerCase()
                : profileStatusRaw;

            if (profileStatus === "declined") {
              setIsCheckingStatus(false);
              setAuthErrorMessage(DECLINED_FUNDRAISER_MESSAGE);
              setFieldError("password", DECLINED_FUNDRAISER_MESSAGE);
              formik.setTouched({ password: true }, false);
              return;
            }

            if (profileStatus === "rejected") {
              setIsCheckingStatus(false);
              setAuthErrorMessage(REJECTED_FUNDRAISER_MESSAGE);
              setFieldError("password", REJECTED_FUNDRAISER_MESSAGE);
              formik.setTouched({ password: true }, false);
              return;
            }

            if (profileStatus === "disabled") {
              setIsCheckingStatus(false);
              setAuthErrorMessage(DISABLED_FUNDRAISER_MESSAGE);
              setFieldError("password", DISABLED_FUNDRAISER_MESSAGE);
              formik.setTouched({ password: true }, false);
              return;
            }

            if (profileStatus === "deleted") {
              setIsCheckingStatus(false);
              setAuthErrorMessage(DELETED_FUNDRAISER_MESSAGE);
              setFieldError("password", DELETED_FUNDRAISER_MESSAGE);
              formik.setTouched({ password: true }, false);
              return;
            }

            // Allow both "approved" and "live" statuses to login
            const allowedStatuses = ["approved", "live"];
            if (!allowedStatuses.includes(profileStatus)) {
              setIsCheckingStatus(false);
              setFieldError(
                "password",
                "Your account is pending admin approval. Please wait for approval.",
              );
              formik.setTouched({ password: true }, false);
              return;
            }
          } catch (profileError) {
            setIsCheckingStatus(false);
            setFieldError(
              "password",
              "Failed to verify account status. Please try again.",
            );
            formik.setTouched({ password: true }, false);
            return;
          }

          const fetchStripeStatus = async () => {
            const stripeStatusResponse = await axios.get(
              "payout/onboardingStatus",
              {
                headers: {
                  Authorization: `Bearer ${tempToken}`,
                  "api-key": process.env.REACT_APP_API_KEY,
                },
              },
            );
            const stripeStatus = stripeStatusResponse?.data?.data;
            return {
              detailsSubmitted: stripeStatus?.detailsSubmitted === true,
              chargesEnabled: stripeStatus?.chargesEnabled === true,
            };
          };

          let stripeDetailsSubmitted = false;
          let stripeChargesEnabled = false;
          try {
            const stripeStatus = await fetchStripeStatus();
            stripeDetailsSubmitted = stripeStatus.detailsSubmitted;
            stripeChargesEnabled = stripeStatus.chargesEnabled;
          } catch (stripeError) {
            clearFundraiserSessionState();
            await loginWithRedux();
            setIsCheckingStatus(false);
            navigate("/fundraiser/welcome", { replace: true });
            return;
          }

          const stripeComplete = stripeDetailsSubmitted && stripeChargesEnabled;
          if (!stripeComplete) {
            clearFundraiserSessionState();
            await loginWithRedux();
            setIsCheckingStatus(false);

            if (stripeDetailsSubmitted && !stripeChargesEnabled) {
              navigate("/complete-setup", { replace: true });
            } else {
              navigate("/fundraiser/welcome", { replace: true });
            }
            return;
          }
        }

        // Step 3: For non-fundraisers or approved fundraisers (handled above) - store token in Redux
        // We already have the token from the first API call, just store it directly
        if (!promiseResult?.data?.token) {
          setIsCheckingStatus(false);
          setFieldError("email", "Failed to receive authentication token");
          return;
        }

        if (normalizedRole === "fundraiser") {
          sessionStorage.setItem("fundraiser_verified", "true");
        } else {
          clearFundraiserSessionState();
        }

        // Store token data in Redux (this will be persisted by redux-persist)
        dispatch(setUserData(promiseResult.data));

        // ALSO save directly to localStorage as backup to ensure axios interceptor can find it
        try {
          const existingState = JSON.parse(
            localStorage.getItem("persist:root") || "{}",
          );
          const authState =
            typeof existingState.authReducer === "string"
              ? JSON.parse(existingState.authReducer)
              : existingState.authReducer;

          const updatedAuthState = {
            ...authState,
            tokenData: promiseResult.data,
          };

          localStorage.setItem(
            "persist:root",
            JSON.stringify({
              ...existingState,
              authReducer: JSON.stringify(updatedAuthState),
            }),
          );
        } catch (e) {}

        // Small delay to ensure redux-persist saves the token to localStorage
        await new Promise((resolve) => setTimeout(resolve, 100));

        setIsCheckingStatus(false); // Hide loader

        const merchantRole = promiseResult?.data?.user?.role;
        const normalizedMerchantRole =
          typeof merchantRole === "string"
            ? merchantRole.toLowerCase()
            : merchantRole;

        if (normalizedMerchantRole === "merchant") {
          const merchantProfiles = promiseResult?.data?.user?.merchantProfiles;
          const activeMerchantProfile =
            getActiveMerchantProfile(merchantProfiles);

          if (!activeMerchantProfile) {
            const normalizedMerchantError = normalizeAuthErrorMessage(
              "",
              merchantProfiles,
            );
            setIsCheckingStatus(false);
            setAuthErrorMessage(
              normalizedMerchantError || DISABLED_MERCHANT_MESSAGE,
            );
            setFieldError(
              "password",
              normalizedMerchantError || DISABLED_MERCHANT_MESSAGE,
            );
            formik.setTouched({ password: true }, false);
            return;
          }

          if (activeMerchantProfile) {
            dispatch(setCurrentBusiness(activeMerchantProfile));
          }
        }

        // Step 4: Navigate directly
        navigation(promiseResult?.data?.user?.role, promiseResult?.data?.user);
        dispatch(
          setLoginData({
            email: credentials.email,
            password: credentials.password,
            fcmToken: credentials.fcmToken,
          }),
        );
      } catch (err) {
        clearFundraiserSessionState();
        setIsCheckingStatus(false); // Hide loader on error
        // Normalize error for UX: email not found vs wrong password
        const status = err?.response?.status;
        const message = normalizeAuthErrorMessage(
          err?.response?.data?.message || err?.message || "Invalid credentials",
        );
        if (status === 404 || /email/i.test(message)) {
          setFieldError("email", "Email doesn’t exist");
        } else if (status === 401) {
          setFieldError("password", "Incorrect password");
        } else {
          setAuthErrorMessage(message);
          setFieldError("password", message);
          formik.setTouched({ password: true }, false);
        }
      }
    },
  });

  const { email, password } = formik.values;

  const { handleBlur, handleSubmit, errors, touched } = formik;
  const inlinePasswordMessage = authErrorMessage || errors.password;

  const handleInputChange = (event) => {
    if (authErrorMessage) {
      setAuthErrorMessage("");
    }
    formik.handleChange(event);
  };

  return (
    <form onSubmit={handleSubmit}>
      {subLoading || authLoading || isCheckingStatus ? <Loader /> : null}
      <div className="min-h-screen bg-white flex flex-col">
        <div className="w-full px-6 py-4">
          <div style={responsiveShellStyle}>
            <LoginHeader />
          </div>
        </div>

        <div className="flex-1 w-full flex flex-col items-center px-4">
          <div className="w-full max-w-[640px] mt-16">
            <h2 className="text-center text-[26px] md:text-[30px] font-[700] font-poppins text-[#303030]">
              Login
            </h2>
            <p className="text-center text-[#8C8C8C] mt-1 text-[14px] md:text-[15px]">
              Please provide your registered email to continue.
            </p>

            <div className="mt-6 flex flex-col items-center">
              <div className="w-[360px] sm:w-[420px] md:w-[460px]">
                <TextInput
                  name={"email"}
                  value={email}
                  placeholder={"Enter your email"}
                  inputClass={
                    "h-full bg-[#F2F4F5] border border-[#EFF2F5] rounded-[12px] pl-8 pr-4 text-[16px] font-poppins font-[500] placeholder:text-[#8C8C8C]"
                  }
                  innerDivClass={"h-[56px] w-full items-center"}
                  handleChange={handleInputChange}
                  handleBlur={handleBlur}
                  error={errors.email}
                  touched={touched.email}
                  autoComplete={"email"}
                />
              </div>

              <div className="w-[360px] sm:w-[420px] md:w-[460px] mt-4">
                <TextInput
                  type={showPassword ? "text" : "password"}
                  name={"password"}
                  value={password}
                  placeholder={"Enter your password"}
                  inputClass={
                    "h-full bg-[#F2F4F5] border border-[#EFF2F5] rounded-[12px] pl-8 pr-4 text-[16px] font-poppins font-[500] placeholder:text-[#8C8C8C]"
                  }
                  innerDivClass={"h-[56px] w-full items-center"}
                  handleChange={handleInputChange}
                  handleBlur={handleBlur}
                  error={inlinePasswordMessage ? "" : errors.password}
                  touched={touched.password}
                  reserveErrorSpace={!inlinePasswordMessage}
                  showPassword={showPassword}
                  togglePassword={togglePassword}
                  autoComplete={"current-password"}
                />
              </div>

              {inlinePasswordMessage ? (
                <div className="w-[360px] sm:w-[420px] md:w-[460px] mt-1 mb-0">
                  <p className="block text-red-600 text-[11px] sm:text-[12px] md:text-[13px] font-[600] font-poppins leading-[1.5]">
                    {inlinePasswordMessage}
                  </p>
                </div>
              ) : null}

              <div className="w-[360px] sm:w-[420px] md:w-[460px] mt-4 flex items-center justify-between gap-6 overflow-visible text-[13px] md:text-[14px] text-[#303030] font-poppins">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 leading-none whitespace-nowrap p-0 m-0 bg-transparent border-0 shrink-0"
                  onClick={onChecked}
                >
                  {checked ? (
                    <CheckedBox className="inputCheckbox" />
                  ) : (
                    <UncheckedBox className="inputCheckbox" />
                  )}
                  <span>Remember me</span>
                </button>
                <NavLink to="/forget-password" className="text-[#00ADEF]">
                  Forget Password
                </NavLink>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <PrimaryButton
                type="submit"
                className="w-[360px] sm:w-[420px] md:w-[460px] h-[60px] rounded-[8px] font-[600]"
                label="Log In"
              />
            </div>

            <div className="mt-4 text-center text-[14px] md:text-[15px] text-[#303030] font-poppins">
              Don't have an account?
              <span className="ml-2 font-[500] text-[#303030]">
                Register as
              </span>
              <NavLink
                to="/merchant/getting-started"
                className="ml-2 text-[#00ADEF] font-[600]"
              >
                Merchant
              </NavLink>
              <span className="mx-1 text-[#00ADEF]">/</span>
              <NavLink
                to="/fundraiser/getting-started"
                className="text-[#00ADEF] font-[600]"
              >
                Fundraiser
              </NavLink>
            </div>
          </div>
        </div>

        <div
          className={`mt-24 mb-8 w-full px-6 login-footer-shell ${footerReady ? "login-footer-shell--visible" : ""}`}
        >
          <SwoodleFooter
            containerStyle={responsiveShellStyle}
            className="login-footer-widget"
            enableEntranceAnimation={true}
          />
        </div>
      </div>
    </form>
  );
};

export default LoginSection;
